import { useEffect, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { AppAccessSystem } from '../../services/api/appAccess';

export interface BulkSummaryItem {
  system: AppAccessSystem;
  systemDisplayName: string;
  count: number;
  sampleUserLabels: string[];
  remainingCount: number;
}

export interface BulkSkippedSummary {
  alreadyInTargetState: number;
  pending: number;
  ineligible: number;
  autoManaged: number;
}

export interface BulkSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  direction: 'grant' | 'revoke';
  totalCount: number;
  perSystem: BulkSummaryItem[];
  skipped: BulkSkippedSummary;
  isLoading: boolean;
}

export function BulkSummaryModal(props: BulkSummaryModalProps) {
  const isRevoke = props.direction === 'revoke';
  const [typedCount, setTypedCount] = useState('');

  useEffect(() => {
    if (props.isOpen) setTypedCount('');
  }, [props.isOpen]);

  const isConfirmEnabled = isRevoke
    ? typedCount.trim() === String(props.totalCount) && !props.isLoading
    : !props.isLoading;

  const verb = isRevoke ? 'Revoke access' : 'Grant access';
  const cellsWord = props.totalCount !== 1 ? 'cells' : 'cell';
  const heading = `${verb} — ${props.totalCount} ${cellsWord}`;

  const skippedTotal =
    props.skipped.alreadyInTargetState +
    props.skipped.pending +
    props.skipped.ineligible +
    props.skipped.autoManaged;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="md"
      ariaLabelledBy="bulk-summary-title"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={props.onClose} disabled={props.isLoading}>
            Cancel
          </Button>
          <Button
            variant={isRevoke ? 'danger' : 'primary'}
            onClick={props.onConfirm}
            disabled={!isConfirmEnabled}
            isLoading={props.isLoading}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <h2 id="bulk-summary-title" className="text-lg font-semibold text-gray-900">
        {heading}
      </h2>

      <div className="mt-4 space-y-3">
        {props.perSystem.map((item) => (
          <div key={item.system}>
            <p className="text-sm font-semibold text-gray-700">
              {item.systemDisplayName} ({item.count})
            </p>
            <ul className="mt-1 ml-4 text-xs text-gray-600 list-disc space-y-0.5">
              {item.sampleUserLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
              {item.remainingCount > 0 && (
                <li className="text-gray-400">… +{item.remainingCount} more</li>
              )}
            </ul>
          </div>
        ))}
      </div>

      {skippedTotal > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          Skipped: {props.skipped.alreadyInTargetState} already in target state,{' '}
          {props.skipped.pending} pending, {props.skipped.ineligible} ineligible,{' '}
          {props.skipped.autoManaged} auto-managed (SSO/SCIM).
        </p>
      )}

      {isRevoke && (
        <>
          <label
            htmlFor="bulk-revoke-confirm"
            className="block text-sm font-semibold text-gray-700 mt-4 mb-1"
          >
            Type {props.totalCount} to confirm revoking {props.totalCount} {cellsWord}.
          </label>
          <input
            id="bulk-revoke-confirm"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={typedCount}
            onChange={(e) => setTypedCount(e.target.value)}
            placeholder={String(props.totalCount)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                       text-gray-900 placeholder-gray-400 focus:border-primary-500
                       focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </>
      )}
    </Modal>
  );
}
